// Gebetszeiten: eigene Zeitpläne der Moschee (CSV/Excel) und Anbieter (vaktija.ba). Ausgeliefert über das Manifest.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { qkPrayer, uploadTimetable, usePrayerProviders, usePrayerTables, type ImportReport, type PrayerTimetable } from '../../api/prayer';
import { can, useMe } from '../../api/queries';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import { Confirm, Dialog, Empty, Field, formatDateTime, PageHead, Spinner } from '../../components/ui';
import { localeOf } from '../../i18n';
import '../../styles/prayer.css';

const TEMPLATE_URL = '/api/v1/prayer/template.csv';
const NAMES = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;

export function PrayerTimesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'de';
  const { data: me } = useMe();
  const { data: tables, isLoading } = usePrayerTables();
  const { data: providers = [] } = usePrayerProviders();
  const qc = useQueryClient();
  const toast = useToast();
  const manage = can(me, 'layouts.manage');
  const [upload, setUpload] = useState<PrayerTimetable | 'new' | null>(null);
  const [rename, setRename] = useState<PrayerTimetable | null>(null);
  const [del, setDel] = useState<PrayerTimetable | null>(null);

  return (
    <>
      <PageHead title={t('prayer.title')} sub={t('prayer.sub')}
        actions={<>
          <a className="btn" href={TEMPLATE_URL} download><Icon name="down" />{t('prayer.template')}</a>
          {manage && <button className="btn primary" onClick={() => setUpload('new')}><Icon name="upload" />{t('prayer.upload')}</button>}
        </>} />
      <div className="card prayer-howto">
        <b>{t('prayer.howTitle')}</b>
        <ol className="small">
          <li>{t('prayer.how1')}</li>
          <li>{t('prayer.how2')}</li>
          <li>{t('prayer.how3')}</li>
        </ol>
      </div>
      <h2 className="prayer-h">{t('prayer.tables')}</h2>
      {isLoading ? <Spinner /> : !tables?.length ? (
        <div className="card"><Empty icon="moon" title={t('prayer.empty')} text={t('prayer.emptyText')} /></div>
      ) : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr><th>{t('common.name')}</th><th>{t('prayer.period')}</th><th>{t('prayer.days')}</th><th>{t('prayer.iqamaCol')}</th><th>{t('prayer.updated')}</th><th /></tr></thead>
            <tbody>
              {tables.map((x) => (
                <tr key={x.id}>
                  <td><b>{x.name}</b>{x.fileName ? <div className="muted small">{x.fileName}</div> : null}</td>
                  <td className="muted">{x.firstDate} – {x.lastDate}</td>
                  <td>{x.dayCount}</td>
                  <td>{x.hasIqama ? t('common.yes') : '–'}</td>
                  <td className="muted">{formatDateTime(x.updatedAt, localeOf(i18n.language))}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {manage && <button className="btn sm ghost" onClick={() => setUpload(x)}>{t('prayer.replace')}</button>}
                    {manage && <button className="btn sm ghost icon" aria-label={t('common.rename')} title={t('common.rename')} onClick={() => setRename(x)}><Icon name="edit" /></button>}
                    {manage && <button className="btn sm ghost icon danger" aria-label={t('common.delete')} title={t('common.delete')} onClick={() => setDel(x)}><Icon name="trash" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h2 className="prayer-h">{t('prayer.providers')}</h2>
      <div className="prayer-providers">
        {providers.map((p) => (
          <div key={p.key} className="card">
            <div className="row"><b className="grow">{p.name[lang]}</b><span className={`pill ${p.enabled ? 'ok' : ''}`}>{p.enabled ? t('prayer.providerOn') : t('prayer.providerOff')}</span></div>
            <div className="small muted">{p.description[lang]}</div>
            <div className="small">{t('prayer.providerLocations', { count: p.locations.length })} · {p.timezone}</div>
            <div className="small muted">{t('prayer.f.attribution', { text: p.attribution })}</div>
          </div>
        ))}
        <div className="card muted small">{t('prayer.providerMore')}</div>
      </div>

      {upload && <UploadDialog existing={upload === 'new' ? null : upload} onClose={() => setUpload(null)} />}
      {rename && <RenameDialog table={rename} onClose={() => setRename(null)} />}
      {del && (
        <Confirm danger title={t('common.delete')} text={t('prayer.confirmDelete', { name: del.name })} confirmLabel={t('common.delete')} onClose={() => setDel(null)}
          onConfirm={async () => {
            try {
              await api.del(`/prayer/tables/${del.id}`);
              await qc.invalidateQueries({ queryKey: qkPrayer.tables });
            } catch (e) {
              toast.error(e);
            }
            setDel(null);
          }} />
      )}
    </>
  );
}

function RenameDialog({ table, onClose }: { table: PrayerTimetable; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(table.name);
  const save = async () => {
    try {
      await api.patch(`/prayer/tables/${table.id}`, { name: name.trim() });
      await qc.invalidateQueries({ queryKey: qkPrayer.tables });
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('common.rename')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim()} onClick={save}>{t('common.save')}</button>
    </>}>
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) void save(); }}>
        <Field label={t('common.name')}><input autoFocus value={name} onChange={(e) => setName(e.target.value)} /></Field>
      </form>
    </Dialog>
  );
}

/** Hochladen: Datei wählen → Prüfbericht (Probelauf) → speichern. */
function UploadDialog({ existing, onClose }: { existing: PrayerTimetable | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(existing?.name ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [rep, setRep] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async (f: File) => {
    setFile(f);
    setRep(null);
    if (!name.trim()) setName(f.name.replace(/\.(csv|txt|xlsx)$/i, ''));
    setBusy(true);
    try {
      setRep(await uploadTimetable(f, name.trim() || f.name, { ...(existing ? { id: existing.id } : {}), dryRun: true }));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadTimetable(file, name.trim(), existing ? { id: existing.id } : {});
      if (!r.ok) { setRep(r); return; }
      await qc.invalidateQueries({ queryKey: qkPrayer.tables });
      await qc.invalidateQueries({ queryKey: ['prayer-data'] });
      toast.show(t('prayer.saved', { count: r.dayCount }));
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog wide title={existing ? `${t('prayer.replace')}: ${existing.name}` : t('prayer.upload')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={busy || !rep?.ok || !name.trim()} onClick={save}>{t('common.save')}</button>
    </>}>
      <div className="col prayer-upload">
        <Field label={t('common.name')}><input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('prayer.namePh')} /></Field>
        <Field label={t('prayer.file')} hint={t('prayer.fileHint')}>
          <input type="file" accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void check(f); }} />
        </Field>
        <a className="small" href={TEMPLATE_URL} download>{t('prayer.templateHint')}</a>
        {busy && <Spinner />}
        {rep && <Report rep={rep} />}
      </div>
    </Dialog>
  );
}

function Report({ rep }: { rep: ImportReport }) {
  const { t } = useTranslation();
  return (
    <div className="prayer-report" role="status">
      {rep.ok
        ? <div className="prayer-ok"><Icon name="check" /> {t('prayer.reportOk', { count: rep.dayCount, first: rep.firstDate, last: rep.lastDate })}{rep.hasIqama ? ` · ${t('prayer.withIqama')}` : ''}</div>
        : <div className="prayer-bad"><Icon name="alert" /> {t('prayer.reportBad', { count: rep.errors.length })}</div>}
      {rep.errors.length > 0 && (
        <ul className="prayer-errors">
          {rep.errors.slice(0, 50).map((e, i) => (
            <li key={i}>{e.row > 0 ? <b>{t('prayer.row', { row: e.row })}{e.column ? ` · ${e.column}` : ''}: </b> : null}{e.message}</li>
          ))}
          {rep.errors.length > 50 && <li className="muted">{t('prayer.moreErrors', { count: rep.errors.length - 50 })}</li>}
        </ul>
      )}
      {rep.warnings.length > 0 && <ul className="prayer-warnings small">{rep.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
      {rep.columns.length > 0 && (
        <div className="small muted">{t('prayer.columns')}: {rep.columns.map((c) => `${c.header} → ${c.meaning}`).join(' · ')}</div>
      )}
      {rep.preview.length > 0 && (
        <div className="scroll">
          <table className="list prayer-preview">
            <thead><tr><th>{t('prayer.date')}</th>{NAMES.map((n) => <th key={n}>{t(`prayer.p.${n}`)}</th>)}</tr></thead>
            <tbody>
              {rep.preview.map((d) => (
                <tr key={d.date}><td>{d.date}</td>{d.times.map((x, i) => <td key={i}>{x}{d.iqama?.[i] ? <div className="muted small">{d.iqama[i]}</div> : null}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
